import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// project-imports
import ThemeModeToggle from './ThemeModeToggle';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import DrawerHeader from 'layout/Dashboard/Drawer/DrawerHeader';
import menuItems from 'menu-items';
import type { NavItemType } from 'types/menu';
import { useCuestionariosStore } from 'features/Cuestionarios/hooks/useCuestionariosStore';
import useLocalStorage from 'hooks/useLocalStorage';
import { useHeaderActions } from 'hooks/useHeaderActions';
import { PermissionGate } from 'components/PermissionGate';
import { clacUiGlobal as ui } from 'utils/uiGlobal';

// ==============================|| HEADER - CONTENT ||============================== //

const sentenceCase = (value: string, locale: string) => {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  const lower = trimmed.toLocaleLowerCase(locale);
  return lower.charAt(0).toLocaleUpperCase(locale) + lower.slice(1);
};

const findBreadcrumb = (items: NavItemType[], pathname: string): { sectionId?: string; itemId?: string } => {
  if (/^\/admin\/usuarios\/(grupos|usuarios|roles-permisos)$/.test(pathname)) {
    return { sectionId: 'menu.group.administracion', itemId: 'menu.item.usuarios' };
  }

  for (const root of items) {
    if (root.type === 'group' && root.children) {
      for (const child of root.children) {
        if (child.type === 'item' && child.url === pathname) return { sectionId: root.title, itemId: child.title };
        if (child.type === 'collapse' && child.children) {
          const found = findBreadcrumb(child.children, pathname);
          if (found.itemId) return { sectionId: root.title, itemId: found.itemId };
        }
      }
    }
  }
  return {};
};

export default function HeaderContent() {
  const { menuOrientation } = useConfig();
  const { headerActions } = useHeaderActions();
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { store } = useCuestionariosStore();

  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const cuestionarioBuilderMatch = useMemo(() => /^\/cuestionarios\/([^/]+)$/.exec(location.pathname), [location.pathname]);
  const cuestionarioHistoryMatch = useMemo(() => /^\/cuestionarios\/([^/]+)\/historial$/.exec(location.pathname), [location.pathname]);
  const cuestionarioWizardMatch = useMemo(() => /^\/cuestionarios\/nuevo(\/plantilla)?$/.test(location.pathname), [location.pathname]);
  const evaluacionesWizardMatch = useMemo(() => /^\/evaluaciones\/nuevo$/.test(location.pathname), [location.pathname]);
  const cuestionarioId = (cuestionarioHistoryMatch?.[1] ?? cuestionarioBuilderMatch?.[1] ?? '') as string;
  const isCuestionarioHistory = Boolean(cuestionarioHistoryMatch);

  const cuestionarioNombre = useMemo(() => {
    if (!cuestionarioId) return '';
    const builder = store.builders[cuestionarioId];
    if (builder?.nombre) return builder.nombre;
    const summary = store.cuestionarios.find((q: { id: string; nombre?: string }) => q.id === cuestionarioId);
    return summary?.nombre ?? '';
  }, [cuestionarioId, store.builders, store.cuestionarios]);

  const breadcrumb = useMemo(
    () => (cuestionarioId || cuestionarioWizardMatch || evaluacionesWizardMatch ? {} : findBreadcrumb(menuItems.items, location.pathname)),
    [cuestionarioId, cuestionarioWizardMatch, evaluacionesWizardMatch, location.pathname]
  );
  const sectionLabel = useMemo(
    () => {
      if (cuestionarioId || cuestionarioWizardMatch) return sentenceCase(intl.formatMessage({ id: 'menu.item.cuestionarios' }), intl.locale);
      if (evaluacionesWizardMatch) return sentenceCase(intl.formatMessage({ id: 'menu.item.evaluaciones' }), intl.locale);
      return breadcrumb.sectionId ? sentenceCase(intl.formatMessage({ id: breadcrumb.sectionId }), intl.locale) : '';
    },
    [breadcrumb.sectionId, cuestionarioId, cuestionarioWizardMatch, evaluacionesWizardMatch, intl]
  );
  const middleLabel = useMemo(() => {
    if (!isCuestionarioHistory) return '';
    return cuestionarioNombre || intl.formatMessage({ id: 'cuestionarios.builder.loading' });
  }, [cuestionarioNombre, intl, isCuestionarioHistory]);

  const itemLabel = useMemo(() => {
    if (cuestionarioWizardMatch) return intl.formatMessage({ id: 'cuestionarios.wizard.new' });
    if (evaluacionesWizardMatch) return intl.formatMessage({ id: 'evaluaciones.wizard.new' });
    if (cuestionarioId && isCuestionarioHistory) return intl.formatMessage({ id: 'cuestionarios.history.page.title' });
    if (cuestionarioId) return cuestionarioNombre || intl.formatMessage({ id: 'cuestionarios.builder.loading' });
    return breadcrumb.itemId ? intl.formatMessage({ id: breadcrumb.itemId }) : '';
  }, [breadcrumb.itemId, cuestionarioId, cuestionarioNombre, cuestionarioWizardMatch, evaluacionesWizardMatch, intl, isCuestionarioHistory]);

  const [evalDraftStatus] = useLocalStorage<{ state: 'draft' | 'saved'; label: string }>(
    'evaluaciones.wizard.draft',
    useMemo(() => ({ state: 'draft', label: intl.formatMessage({ id: 'evaluaciones.wizard.draft' }) }), [intl])
  );

  return (
    <>
      {menuOrientation === MenuOrientation.HORIZONTAL && !downLG && <DrawerHeader open={true} />}
      {(!!sectionLabel || !!itemLabel) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1 }}>
          <Box aria-hidden={true} sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: ui.colors.green[400], flexShrink: 0 }} />
          <Box component="nav" aria-label="breadcrumb" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!!sectionLabel && (
              <Typography
                onClick={evaluacionesWizardMatch ? () => navigate('/evaluaciones') : undefined}
                sx={{
                  fontSize: '0.8125rem',
                  color: ui.semantic.text.muted,
                  fontWeight: 400,
                  lineHeight: 1.2,
                  ...(evaluacionesWizardMatch
                    ? { cursor: 'pointer', '&:hover': { color: ui.semantic.accent.main, textDecoration: 'underline' } }
                    : null)
                }}
              >
                {sectionLabel}
              </Typography>
            )}
            {!!sectionLabel && !!middleLabel && (
              <Typography aria-hidden={true} sx={{ fontSize: '0.8125rem', color: ui.semantic.text.muted, lineHeight: 1 }}>
                ›
              </Typography>
            )}
            {!!middleLabel && (
              <Typography
                onClick={() => navigate(`/cuestionarios/${cuestionarioId}`)}
                sx={{ fontSize: '0.8125rem', color: ui.semantic.text.muted, fontWeight: 400, lineHeight: 1.2, cursor: 'pointer', '&:hover': { color: ui.semantic.accent.main, textDecoration: 'underline' } }}
              >
                {middleLabel}
              </Typography>
            )}
            {!!sectionLabel && !!itemLabel && (
              <Typography aria-hidden={true} sx={{ fontSize: '0.8125rem', color: ui.semantic.text.muted, lineHeight: 1 }}>
                ›
              </Typography>
            )}
            {!!itemLabel && (
              <Typography
                sx={{
                  fontFamily: `'Plus Jakarta Sans', sans-serif`,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: ui.semantic.text.primary,
                  lineHeight: 1.2
                }}
              >
                {itemLabel}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      <Box sx={{ flexGrow: 1 }} />
      {headerActions}
      {evaluacionesWizardMatch ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 1.5 }}>
          <Box
            aria-hidden={true}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: evalDraftStatus?.state === 'saved' ? ui.colors.green[400] : ui.semantic.border.default,
              ...(evalDraftStatus?.state === 'saved'
                ? { animation: 'none' }
                : {
                    animation: 'ddPulse 2s infinite',
                    '@keyframes ddPulse': {
                      '0%,100%': { opacity: 1 },
                      '50%': { opacity: 0.4 }
                    }
                  })
            }}
          />
          <Typography sx={{ fontSize: '0.75rem', color: ui.semantic.text.muted, fontWeight: 500, whiteSpace: 'nowrap' }}>
            {evalDraftStatus?.label ?? intl.formatMessage({ id: 'evaluaciones.wizard.draft' })}
          </Typography>
        </Box>
      ) : null}
      <ThemeModeToggle />
    </>
  );
}
